import { UserManager, WebStorageStateStore, User, SignoutResponse } from 'oidc-client';
import { ApplicationName } from './ApiAuthorizationConstants';

class AuthorizeService { // Removed 'export' keyword from class
  _callbacks: { callback: Function; subscription: number }[] = [];
  _nextSubscriptionId: number = 0;
  _user: User | null = null;
  _isAuthenticated: boolean = false;
  _popUpDisabled: boolean = true;
  userManager: UserManager | undefined;

  async isAuthenticated(): Promise<boolean> {
    const user = await this.getUser();
    return !!user;
  }

  async getUser(forceRefresh: boolean = false): Promise<User | null> {
    if (this._user && !forceRefresh) {
      return this._user;
    }

    await this.ensureUserManagerInitialized();
    const user = await this.userManager!.getUser();
    if (user) {
      this._user = user;
    }
    return user;
  }

  async getAccessToken(): Promise<string | undefined> {
    await this.ensureUserManagerInitialized();
    const user = await this.userManager!.getUser();
    return user?.access_token; // Use optional chaining
  }

  async signIn(state: any): Promise<AuthenticationResult> {
    await this.ensureUserManagerInitialized();
    try {
      const silentUser = await this.userManager!.signinSilent(this.createArguments());
      this.updateState(silentUser);
      return this.success(state);
    } catch (silentError) {
      try {
        if (this._popUpDisabled) {
          throw new Error('Popup disabled. Change \'AuthorizeService.js:AuthorizeService._popupDisabled\' to false to enable it.')
        }

        const popUpUser = await this.userManager!.signinPopup(this.createArguments());
        this.updateState(popUpUser);
        return this.success(state);
      } catch (popUpError: any) {
        if (popUpError.message === "Popup window closed") {
          return this.error("The user closed the window.");
        }

        try {
          await this.userManager!.signinRedirect(this.createArguments(state));
          return this.redirect();
        } catch (redirectError: any) {
          return this.error(redirectError);
        }
      }
    }
  }

  async loginWithCredentials(email: string, password: string, rememberMe: boolean, returnUrl: string): Promise<AuthenticationResult> {
    try {
      const response = await fetch('/api/Account/Login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, rememberMe, returnUrl })
      });

      if (response.ok) {
        await this.ensureUserManagerInitialized();

        try {
          const user = await this.userManager!.signinSilent(this.createArguments());
          this.updateState(user);
          return this.success({ returnUrl });
        } catch (silentError) {
          // Даже если signinSilent не сработал, куки установлены, поэтому считаем успехом
          return this.success({ returnUrl });
        }
      } else {
        const errorData = await response.json();
        return this.error(errorData.message || 'Login failed.');
      }
    } catch (error: any) {
      return this.error(error.message || 'An unexpected error occurred.');
    }
  }

  async completeSignIn(url: string): Promise<AuthenticationResult> {
    try {
      await this.ensureUserManagerInitialized();
      const user = await this.userManager!.signinCallback(url);
      this.updateState(user);
      return this.success(user && user.state);
    } catch (error) {
      return this.error('There was an error signing in.');
    }
  }

  async signOut(state: any): Promise<AuthenticationResult> {
    await this.ensureUserManagerInitialized();
    try {
      if (this._popUpDisabled) {
        throw new Error('Popup disabled. Change \'AuthorizeService.js:AuthorizeService._popupDisabled\' to false to enable it.')
      }

      await this.userManager!.signoutPopup(this.createArguments());
      this.updateState(undefined);
      return this.success(state);
    } catch (popupSignOutError) {
      try {
        await this.userManager!.signoutRedirect(this.createArguments(state));
        return this.redirect();
      } catch (redirectSignOutError: any) {
        return this.error(redirectSignOutError);
      }
    }
  }

  async completeSignOut(url: string): Promise<AuthenticationResult> {
    await this.ensureUserManagerInitialized();
    try {
      const response: SignoutResponse | undefined = await this.userManager!.signoutCallback(url);
      this.updateState(null);
      return this.success(response && response.state);
    } catch (error: any) {
      return this.error(error.message || 'An unexpected error occurred during logout.');
    }
  }

  updateState(user: User | undefined | null) {
    this._user = user || null;
    this._isAuthenticated = !!this._user;
    this.notifySubscribers();
  }

  subscribe(callback: Function): number {
    this._callbacks.push({ callback, subscription: this._nextSubscriptionId++ });
    return this._nextSubscriptionId - 1;
  }

  unsubscribe(subscriptionId: number) {
    const subscriptionIndex = this._callbacks
      .map((element, index) => element.subscription === subscriptionId ? { found: true, index } : { found: false })
      .filter(element => element.found === true);
    if (subscriptionIndex.length !== 1) {
      throw new Error(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
    }

    this._callbacks.splice(subscriptionIndex[0].index!, 1); // Use non-null assertion
  }

  notifySubscribers() {
    for (let i = 0; i < this._callbacks.length; i++) {
      const callback = this._callbacks[i].callback;
      callback();
    }
  }

  createArguments(state?: any) {
    return { useReplaceToNavigate: true, data: state };
  }

  error(message: string): AuthenticationResult {
    return { status: AuthenticationResultStatus.Fail, message };
  }

  success(state: any): AuthenticationResult {
    return { status: AuthenticationResultStatus.Success, state };
  }

  redirect(): AuthenticationResult {
    return { status: AuthenticationResultStatus.Redirect };
  }

  async ensureUserManagerInitialized(): Promise<void> {
    if (this.userManager !== undefined) {
      return;
    }

    // Get the base URL dynamically from the current location
    const baseUrl = window.location.origin;

    const settings = {
        authority: baseUrl,
        client_id: "SmartCashBackEvo",
        redirect_uri: `${baseUrl}/authentication/login-callback`,
        post_logout_redirect_uri: `${baseUrl}/authentication/logout-callback`,
        response_type: "code",
        scope: "openid profile SmartCashBackEvoAPI roles",
        automaticSilentRenew: true,
        includeIdTokenInSilentRenew: true,
        userStore: new WebStorageStateStore({
            prefix: ApplicationName
        })
    };

    this.userManager = new UserManager(settings);

    this.userManager.events.addUserSignedOut(async () => {
      await this.userManager!.removeUser();
      this.updateState(undefined);
    });
  }

  static get instance(): AuthorizeService { return authService }
}

const authService = new AuthorizeService();

export default authService;

export const AuthenticationResultStatus = {
  Redirect: 'redirect',
  Success: 'success',
  Fail: 'fail'
};

export interface AuthenticationResult {
  status: string;
  message?: string;
  state?: any;
}
